/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'util/PropTypes';
const LayoutApi = require('gemini/dashboard/layout/views/LayoutApi');
import { isAlive } from 'mobx-state-tree';

import { observable } from 'mobx';
import { STARTING_POINTS } from '../explorecontainer/ExploreViewModel';

/**
 * Class representing base layout view
 * replaces dashboard core's LayoutBaseView
 * Each LayoutBase view wraps around a 'ReactLayoutImplBase' based view implementation specified by this.Component
 * The root 'LayoutBase' view gets a DOM element and bootstraps a vdom tree
 * To render a 'LayoutBase' hierarchy, each view receives it's children's JSX markup in this.childrenRenderMarkup
 * and then it includes that in it's render JSX markup
 *
 * Each 'LayoutBase' view has the following functions
 *   getRenderMarkup         : returns JSX using this.Component or from this.view (the react view instance)
 *   addChildRenderMarkup    : called by 'LayoutController infrastructure' when a child view is instantiated
 *   getChildrenRenderMarkup : returns the received child render markup
 *
 * In simple terms, 'LayoutController infrastructure' refers to Layout authoring helper class, eventing
 * via CommonAuthoringFeature & LayoutController.createLayoutModel()
 * TODO: reduce dependence on 'LayoutController infrastructure'
 */
export class LayoutBase  {

	constructor(props) {
		this.dashboardApi = props.dashboardApi;
		this.content = props.content;
		this.layoutController = props.layoutController;
		this.parentLayout = props.parentLayout;
		this.authorHelper = props.authorHelper;

		this.model = props.model;
		this.services = props.services;
		this.logger = this.services.biGlass.glassContext.getCoreSvc('.Logger');
		this.Component = this.Component || props.Component;

		this.id = props.model.id;

		//Each layout view may have child layout views.
		//As each child layout view is created and is ready, its render markup gets added to
		//the parent's childrenRenderMarkup
		//This is how react based views get rendered
		this.childrenRenderMarkup = [];

		//When we add a child layout e.g. card gets added to explore container, explore container
		//should be rendered again. When we are adding multiple child layouts at the same time e.g.
		//multi-select and duplicate cards, if we render ExploreContainer again for every child that
		//becomes available, it makes for inefficient rendering
		//If we have a way to only render the parent when all its children layouts are ready, it reduces
		//the number of times parent is rendered
		//
		//shouldRenderCount is used a simple perf tweak to reduce the number of renders
		//shouldRenderCount indicates the number of children whose markup is ready
		//as each child markup is made available, childCount is updated
		//when childCount is equal to the number of children in the model, we know that we have all
		//the children markup ready, so now the parent layout can simply observe changes in shouldRenderCount
		//and render
		this.renderTracker = observable( { shouldRenderCount : 0 });
		this.childCount = 0;

		this.$el = this.layoutController.$el.find(`#${this.id}`); //TODO: get rid of jquery
		this.domNode = this.$el.get(0);

		if (this._isTopLevel()) {
			const reactLayoutClass =  `${this.model.type}-react-layout-view`;
			this.$el.addClass(reactLayoutClass);
		}

		this._alive = true;

		this.api = new LayoutApi(this);
	}

	destroy() {
		clearTimeout(this.resizeTimer);
		this.authorHelper && this.authorHelper.destroy();
		this.removeRenderMarkupFromParent();
		this.layoutController.removeView(this);
		this._alive = false;

		const destroyChild = (item) => {
			if(item) {
				const e = { value: { parameter : item.id } };
				this.layoutController.destroyLayoutView(item.id, e);
			}
		};
		if (this.model.items) {
			this.model.items.forEach(destroyChild);
		}



		if( this._mountedComponent ) {
			ReactDOM.unmountComponentAtNode(this.$el[0]);
		}

		if( this.domNode ) {
			this.domNode._layout = null;
		}
	}

	setViewImpl(view) {
		this.view = view;
	}

	getChildRenderMarkup(childId) {
		return this.childrenRenderMarkup.find((entry) => {
			return (entry.id === childId);
		});
	}

	addChildRenderMarkup(childId, renderMarkup) {
		const found = this.getChildRenderMarkup(childId);
		if(!found) {
			this.childrenRenderMarkup.push({ id: childId, value: renderMarkup });
			this.childCount++;
			this._tagReadyForRender();
		}
		return !found;
	}

	_tagReadyForRender() {
		const numChildren = this.model.items && this.model.items.length || 0;
		if( this.childCount === numChildren ) {
			this.renderTracker.shouldRenderCount = this.childCount;
		}
	}

	removeChildRenderMarkup(childId) {
		this.childrenRenderMarkup = this.childrenRenderMarkup.filter((entry) => {
			return (entry.id !== childId);
		});
		this.childCount--;
		this._tagReadyForRender();
	}

	getChildrenRenderMarkup() {
		return this.childrenRenderMarkup;
	}

	getRenderMarkup() {
		return this.view && this.view.getRenderMarkup();
	}

	addToParentRenderMarkup() {
		if(this.parentLayout) {
			this.parentLayout.addChildRenderMarkup( this.id, this.getRenderMarkup() );
		}
	}
	removeRenderMarkupFromParent() {
		if(this.parentLayout) {
			this.parentLayout.removeChildRenderMarkup(this.id);
		}
	}

	// --- PUBLIC METHODS

	getTemplate() {
		return [
			'<div id="{{id}}" tabindex="-1" class="page {{type}}-react-layout-view{{extraCSS}}"{{style}}>{{items}}</div>'
		];
	}

	getNodeId() {
		console.warn('`getNodeId` called - not implemented yet');
	}


	onResize() {
		// not implemented, we rely on `react-measure` to figure out when the widgets have resized.
	}

	resize() {
		this.resizeChildren(0);
	}

	resizeChildren(timeout) {
		clearTimeout(this.resizeTimer);
		this.resizeTimer = setTimeout(this._onResize.bind(this), timeout);
	}

	_onResize() {
		clearTimeout(this.resizeTimer);

		if(!this._alive) {
			return;
		}

		const handleResize = item => {
			var view = this.layoutController.getLayoutView(item.id);
			if (view && view.onResize) {
				// do not try re-sizing hidden el ements, as their sizes won't be accurate
				if (!view.$el.is(':hidden') &&
					view.widget &&
					view.widget.isFirstRenderComplete &&
					view.widget.isFirstRenderComplete()) {
					view.onResize();
				} else {
					view.resizeOnShow();
				}
			}
		};

		//TODO: can we avoid isAlive check ??
		if(isAlive(this.model)) {
			if (this.model.items) {
				this.model.items.forEach(item => {
					item.items && item.items.forEach(handleResize);
					handleResize(item);
				});
			}
		}
	}

	onSelect() {
		console.warn('`onSelect` called - not implemented yet');
	}

	onDeselect() {
		console.warn('`onDeselect` called - not implemented yet');
	}

	_isTopLevel() {
		return this.model.boardModel.layout.id === this.model.id;
	}

	onShow() {
		if(this.Component && this.view) {
			return; //already mounted nothing to do
		}
		if(!this.$el.length) {
			return; //can't render without a DOM element
		}

		this._beforeOnShowForAllChildren();
		this._callChildrenFunction('addToParentRenderMarkup');

		this.beforeOnShow && this.beforeOnShow();

		this.model.setDisplayStyleOn();

		this._mountedComponent = true;
		ReactDOM.unmountComponentAtNode(this.$el[0]);
		const e = React.createElement(this.Component, {
			items: this.model ? this.model.items : [],
			model: this.model,
			services: this.services,
			layoutController: this.layoutController,
			dashboardApi: this.dashboardApi,
			layoutOwner: this
		});
		ReactDOM.render(
			e,
			this.$el.get(0)
		);

		this.$el.get(0)._layout = this; //to allow LayoutController to destroy top level layout !?#@

	}

	render() {
		this.onShow();
	}

	renderContent(additionalWidgetData) {
		const stateApi = this.content.getFeature('state.internal');
		stateApi.setStatus(stateApi.STATUS.RENDERING);
		// to be implemented by views
		const promises = [];
		if (this.model.items) {
			for (let i = 0; i < this.model.items.length; i++) {
				promises.push(this.layoutController.createLayoutModule(this.model.items[i].id, this, additionalWidgetData).then(layout => layout.renderContent()));
			}
		}
		return Promise.all(promises).then(() => {
			stateApi.setStatus(stateApi.STATUS.RENDERED);
			this.layoutController.markViewAsReady(this);
		});
	}

	removeChild() {
		//no op
	}

	onHide() {
		console.warn('`onHide` called - not implemented yet');
	}

	onTransition() {
		console.warn('`onTransition` called - not implemented yet');
	}

	afterTransition() {
		console.warn('`afterTransition` called - not implemented yet');
	}

	resizeOnShow() {
		console.warn('`resizeOnShow` called - not implemented yet');
	}

	getProperties() {
		console.warn('`getProperties` called - not implemented yet');
	}

	getBannerProperty() {
		console.warn('`getBannerProperty` called - not implemented yet');
	}

	reduce() {
		console.warn('`reduce` called - not implemented yet');
	}

	setLoading() {
		console.warn('`setLoading` called - not implemented yet');
	}

	getSelectedSubViewId() {
		if (this.model.exploreVM.isStartingPointsDisplayed()) {
			return STARTING_POINTS;
		}
		return this.model.exploreVM.getDisplayedCard();
	}

	// --- PRIVATE METHODS

	_callChildrenFunction(name) {
		(this.model.items || [])
			.forEach((item) => {
				var view = this.layoutController.getLayoutView(item.id);
				if (view && view[name]) {
					view[name]();
				}
			});
	}

	_beforeOnShowForAllChildren() {
		(this.model.items || [])
			.forEach((item) => {
				var view = this.layoutController.getLayoutView(item.id);
				if (view ) {
					if(this._beforeOnShow) {
						this._beforeOnShow(item.id, view);
					}
					if( view._beforeOnShowForAllChildren) {
						view._beforeOnShowForAllChildren();
					}
				}
			});
	}

	getAPI() {
		return this.api;
	}
}


/**
 * Class representing react based view implementation for 'LayoutBase' views
 * Keeping the view implemenation separate from 'LayoutBase' layout owner to help with rendering
 * layout view hierarchies
 */
export class ReactLayoutImplBase extends Component {
	constructor(props) {
		super(props);
		this.layoutOwner = props.layoutOwner;
		this.layoutOwner.setViewImpl(this);
		this.model = props.model;
		this.layoutController = props.layoutController;
		this.services = props.services;
		this.dashboardApi = props.dashboardApi;
	}

	getChildrenRenderMarkup() {
		return this.layoutOwner.getChildrenRenderMarkup();
	}

	getRenderMarkup() {
		return {};
	}
}

ReactLayoutImplBase.propTypes = {
	items: PropTypes.array,
	layoutController: PropTypes.object,
	services: PropTypes.object,
	dashboardApi: PropTypes.object,
	parentLayout: PropTypes.object,
	model: PropTypes.object,
	layoutOwner: PropTypes.object.isRequired,
	footerView: PropTypes.object
};



// WEBPACK FOOTER //
// ./src/js/layout/base/LayoutBase.js